import { useState } from "react";
import * as XLSX from "xlsx";
import API from "services/axios";
import authHeader from "services/auth-header";
import { formatSqlDate } from "utils/UtilityFuncs";

function JobBoardExcel({
  filterBy,
  API_CALL,
  filterText,
  selectedLocation,
  dateFrom,
  dateTo,
  sortBy,
  sortDir,
  // The tab's WO status. Only the Pending and All tabs supply one, and
  // /job-board/pending-orders REFUSES a request without it (406, "WO Status is
  // required"), so leaving it out made Export Excel fail on the Pending tab
  // while every other tab worked.
  woStatus = null,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const exportToExcel = async () => {
    if (!API_CALL) {
      setError("No API endpoint provided");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const searchQuery = filterText?.trim();

      const body = {
        query: searchQuery || undefined,
        productTypeId: filterBy?.productTypeId || undefined,
        locationId: selectedLocation || undefined,
        getQty: true,
        // Same filters the card is showing. Without these, exporting a
        // card filtered to This Week hands back the entire board.
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        sortBy: sortBy || undefined,
        sortDir: sortBy ? sortDir : undefined,
      };

      // Added the same way, and for the same reason, as the card's own fetch
      // in JobBoardItem -- the export must ask the endpoint the same question
      // the card asked, or it is not exporting what is on screen.
      if (woStatus) {
        body.WOStatus = woStatus;
      }

      const { data } = await API.post(`${API_CALL}?page=1&limit=10000`, body, {
        headers: authHeader(),
      });

      let records = [];
      if (data?.success) {
        records = data.data || [];
      } else if (data?.type === "success") {
        records = data.result || [];
      } else if (data?.type === "error") {
        setError(data.message || "Failed to fetch data");
        return;
      }

      // Transform data for Excel
      const rows = records.map((item) => ({
        "W-ID": `W-${item?.woId}`,
        "Customer Name": item?.customerName || "",
        "Job Name": item?.jobName || "",
        "Due Date": formatSqlDate({ sqlDate: item?.WoDueDate }) || "",
        Quantity: item?.totalQuantity || 0,
      }));

      // Getting total quantity for all records
      const totalQty = records.reduce(
        (sum, item) => sum + (parseInt(item?.totalQuantity) || 0),
        0,
      );

      // Adding a total row at the end of the data
      rows.push({
        "W-ID": "",
        "Customer Name": "",
        "Job Name": "",
        "Due Date": "Total",
        Quantity: totalQty,
      });

      // Generate Excel Sheet using rows data
      const ws = XLSX.utils.json_to_sheet(rows);

      // Create a new workbook
      const wb = XLSX.utils.book_new();

      // Appending our data sheet to excel workbook
      XLSX.utils.book_append_sheet(wb, ws, filterBy?.productTypes || "Report");

      // Writing the workbook to a file
      XLSX.writeFile(wb, `${filterBy?.productTypes || "JobBoard"}_Report.xlsx`);
    } catch (err) {
      setError(err.message || "Unexpected error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={exportToExcel}
        disabled={loading}
        className="text-decoration-none text-white btn btn-warning me-1 btn-sm"
      >
        <i className="mdi mdi-file-excel"></i>
        {loading ? " Exporting..." : " Export Excel"}
      </button>
      {error && <div className="text-danger small mt-1">{error}</div>}
    </>
  );
}

export default JobBoardExcel;
